import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    // Auth
    appName: 'Organizer Space',
    tagline: 'Helping professional organizers stay organized',
    welcomeBack: 'Welcome back',
    enterCredentials: 'Enter your credentials to continue',
    createAccount: 'Create account',
    getStarted: 'Get started with your organizing journey',
    email: 'Email',
    password: 'Password',
    fullName: 'Full Name',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    creatingAccount: 'Creating account...',
    dontHaveAccount: "Don't have an account?",
    createOne: 'Create one',
    alreadyHaveAccount: 'Already have an account?',
    minPassword: 'Min. 6 characters',
    
    // Navigation
    dashboard: 'Dashboard',
    clients: 'Clients',
    projects: 'Projects',
    intakes: 'Intakes',
    calendar: 'Calendar',
    finances: 'Finances',
    timeExpenses: 'Time & Expenses',
    invoicing: 'Invoicing',
    signOut: 'Sign out',
    yourBestFriend: 'Your best friend',
    
    // Dashboard
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    overviewForToday: "Here's your overview for today",
    totalClients: 'Total Clients',
    activeProjects: 'Active Projects',
    upcomingEvents: 'Upcoming Events',
    netIncome: 'Net Income',
    intakePipeline: 'Intake Overview',
    intakeOverview: 'Intake Overview',
    pendingOffers: 'Pending Offers',
    quarterlyIncome: 'Quarterly Income',
    netIncomeByQuarter: 'Net income per quarter',
    viewAll: 'View all',
    viewCalendar: 'View calendar',
    noUpcomingEvents: 'No upcoming events',
    totalIncome: 'Total Income',
    totalExpenses: 'Total Expenses',
    pendingInvoices: 'Pending Invoices',
    
    // Clients
    totalClientsCount: 'total clients',
    addClient: 'Add Client',
    editClient: 'Edit Client',
    addNewClient: 'Add New Client',
    clientName: 'Name',
    phone: 'Phone',
    address: 'Address',
    notes: 'Notes',
    hourlyRate: 'Hourly Rate',
    searchClients: 'Search clients...',
    noClientsYet: 'No clients yet. Add your first client!',
    noClientsFound: 'No clients found matching your search',
    clientDeleted: 'Client deleted',
    clientAdded: 'Client added',
    clientUpdated: 'Client updated',
    deleteConfirm: 'Delete',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    viewProjects: 'View Projects',
    clientProjects: 'Client Projects',
    noProjectsForClient: 'No projects for this client yet',
    totalHours: 'Total Hours',
    totalBillable: 'Total Billable',
    logHours: 'Log Hours',
    addExpense: 'Add Expense',
    
    // Projects
    totalProjectsCount: 'total projects',
    newProject: 'New Project',
    editProject: 'Edit Project',
    createNewProject: 'Create New Project',
    projectName: 'Project Name',
    client: 'Client',
    selectClient: 'Select a client',
    description: 'Description',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    active: 'Active',
    inProgress: 'In Progress',
    onHold: 'On Hold',
    completed: 'Completed',
    searchProjects: 'Search projects...',
    noProjectsYet: 'No projects yet. Create your first project!',
    noProjectsFound: 'No projects found matching your search',
    projectDeleted: 'Project deleted',
    projectCreated: 'Project created',
    projectUpdated: 'Project updated',
    all: 'All',
    unknownClient: 'Unknown client',
    selectProject: 'Select a project',
    project: 'Project',
    
    // Intakes
    totalIntakesCount: 'total intakes',
    newIntake: 'New Intake',
    editIntake: 'Edit Intake',
    serviceType: 'Service Type',
    stage: 'Stage',
    lead: 'Lead',
    consultation: 'Consultation',
    offerSent: 'Offer Sent',
    offerAccepted: 'Offer Accepted',
    noIntakes: 'No intakes',
    intakeDeleted: 'Intake deleted',
    intakeCreated: 'Intake created',
    intakeUpdated: 'Intake updated',
    moveTo: 'Move to',
    leads: 'Leads',
    
    // Calendar
    eventsScheduled: 'events scheduled',
    addEvent: 'Add Event',
    editEvent: 'Edit Event',
    newEvent: 'New Event',
    eventTitle: 'Event Title',
    start: 'Start',
    end: 'End',
    linkToClient: 'Link to Client (optional)',
    color: 'Color',
    sage: 'Sage',
    clay: 'Clay',
    stone: 'Stone',
    blue: 'Blue',
    none: 'None',
    today: 'Today',
    eventDeleted: 'Event deleted',
    eventCreated: 'Event created',
    eventUpdated: 'Event updated',
    more: 'more',
    
    // Finances
    trackIncomeExpenses: 'Track your income and expenses',
    addTransaction: 'Add Transaction',
    newTransaction: 'New Transaction',
    type: 'Type',
    income: 'Income',
    expense: 'Expense',
    amount: 'Amount',
    date: 'Date',
    category: 'Category',
    selectCategory: 'Select category',
    optionalNotes: 'Optional notes',
    saveTransaction: 'Save Transaction',
    transactionDeleted: 'Transaction deleted',
    incomeRecorded: 'Income recorded',
    expenseRecorded: 'Expense recorded',
    noTransactionsYet: 'No transactions yet',
    transactions: 'Transactions',
    expenses: 'Expenses',
    monthlyOverview: 'Monthly Overview',
    
    // Time & Expenses
    timeAndExpenses: 'Time & Expenses',
    trackTimeAndExpenses: 'Track time and expenses for your projects',
    timeEntries: 'Time Entries',
    expenseEntries: 'Expense Entries',
    addTimeEntry: 'Add Time Entry',
    addExpenseEntry: 'Add Expense Entry',
    hoursWorked: 'Hours Worked',
    hours: 'hours',
    expenseType: 'Expense Type',
    cost: 'Cost',
    products: 'Products',
    travel: 'Travel',
    parking: 'Parking',
    materials: 'Materials',
    noTimeEntries: 'No time entries yet',
    noExpenseEntries: 'No expense entries yet',
    timeEntryAdded: 'Time entry added',
    timeEntryDeleted: 'Time entry deleted',
    expenseEntryAdded: 'Expense entry added',
    expenseEntryDeleted: 'Expense entry deleted',
    
    // Invoicing
    invoices: 'Invoices',
    manageInvoices: 'Manage and generate invoices',
    createInvoice: 'Create Invoice',
    newInvoice: 'New Invoice',
    invoiceNumber: 'Invoice Number',
    dueDate: 'Due Date',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    hoursAmount: 'Hours Amount',
    totalAmount: 'Total Amount',
    generateInvoice: 'Generate Invoice',
    exportPdf: 'Export PDF',
    markAsSent: 'Mark as Sent',
    markAsPaid: 'Mark as Paid',
    invoiceCreated: 'Invoice created',
    invoiceUpdated: 'Invoice updated',
    invoiceDeleted: 'Invoice deleted',
    noInvoicesYet: 'No invoices yet',
    viewDetails: 'View Details',
    invoiceDetails: 'Invoice Details',
    lineItems: 'Line Items',
    subtotal: 'Subtotal',
    
    // Categories
    serviceFee: 'Service Fee',
    productSale: 'Product Sale',
    referralBonus: 'Referral Bonus',
    other: 'Other',
    supplies: 'Supplies',
    marketing: 'Marketing',
    software: 'Software',
    insurance: 'Insurance',
    office: 'Office',
    
    // Common
    required: 'required',
    save: 'Save',
    update: 'Update',
    create: 'Create',
    add: 'Add',
    loading: 'Loading...',
    close: 'Close',
    perHour: 'per hour',
    total: 'Total',
    from: 'From',
    to: 'To',
    
    // Settings
    settings: 'Settings',
    profile: 'Profile',
    accountSettings: 'Account Settings',
    accountInfo: 'Account Information',
    memberSince: 'Member since',
    updateProfile: 'Update Profile',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    passwordsNoMatch: 'Passwords do not match',
    passwordUpdated: 'Password updated successfully',
    profileUpdated: 'Profile updated successfully',
    deleteAccount: 'Delete Account',
    deleteAccountWarning: 'This action cannot be undone. All your data will be permanently deleted.',
    confirmDelete: 'Type DELETE to confirm',
    accountDeleted: 'Account deleted',
    dangerZone: 'Danger Zone',
    // Tasks
    tasks: 'Tasks',
    newTask: 'New Task',
    editTask: 'Edit Task',
    taskTitle: 'Task Title',
    taskCreated: 'Task created',
    taskUpdated: 'Task updated',
    taskDeleted: 'Task deleted',
    noTasks: 'No tasks yet',
    totalTasksCount: 'total tasks',
    selectProject: 'Select project',
    taskOverview: 'Task Overview',
    reportAnIssue: 'Report An Issue',
    signInWithGoogle: 'Sign in with Google',
    signUpWithGoogle: 'Sign up with Google',
    orContinueWith: 'or continue with email',
    googleAccountNote: 'Signed in with Google',
    passwordNotAvailable: 'Password change is not available for Google accounts',
    intakeDate: 'Intake Date',
    followUpDate: 'Follow-up Date',
    followUp: 'Follow-up',
    timeline: 'Timeline',
    projectTimeline: 'Project Timeline',
    noProjectDates: 'No projects with dates yet',
    addDatesToProjects: 'Add start/end dates to see the timeline',
    beforeAfter: 'Before & After',
    followUpReminders: 'Follow-up Reminders',
    noFollowUps: 'No upcoming follow-ups',
    dueToday: 'Due today',
    today: 'Today',
  },
  nl: {
    // Auth
    appName: 'Organizer Space',
    tagline: 'Helpt professionele organizers georganiseerd te blijven',
    welcomeBack: 'Welkom terug',
    enterCredentials: 'Voer uw gegevens in om door te gaan',
    createAccount: 'Account aanmaken',
    getStarted: 'Begin uw organisatiereis',
    email: 'E-mail',
    password: 'Wachtwoord',
    fullName: 'Volledige naam',
    signIn: 'Inloggen',
    signingIn: 'Inloggen...',
    creatingAccount: 'Account aanmaken...',
    dontHaveAccount: 'Nog geen account?',
    createOne: 'Maak er een',
    alreadyHaveAccount: 'Heeft u al een account?',
    minPassword: 'Min. 6 tekens',
    
    // Navigation
    dashboard: 'Dashboard',
    clients: 'Klanten',
    projects: 'Projecten',
    intakes: 'Intakes',
    calendar: 'Agenda',
    finances: 'Financiën',
    timeExpenses: 'Tijd & Kosten',
    invoicing: 'Facturatie',
    signOut: 'Uitloggen',
    yourBestFriend: 'Uw beste vriend',
    
    // Dashboard
    goodMorning: 'Goedemorgen',
    goodAfternoon: 'Goedemiddag',
    goodEvening: 'Goedenavond',
    overviewForToday: 'Hier is uw overzicht voor vandaag',
    totalClients: 'Totaal klanten',
    activeProjects: 'Actieve projecten',
    upcomingEvents: 'Komende evenementen',
    netIncome: 'Netto inkomen',
    intakePipeline: 'Intake Overzicht',
    intakeOverview: 'Intake Overzicht',
    pendingOffers: 'Openstaande offertes',
    quarterlyIncome: 'Kwartaal inkomen',
    netIncomeByQuarter: 'Netto inkomen per kwartaal',
    viewAll: 'Bekijk alles',
    viewCalendar: 'Bekijk agenda',
    noUpcomingEvents: 'Geen komende evenementen',
    totalIncome: 'Totale inkomsten',
    totalExpenses: 'Totale uitgaven',
    pendingInvoices: 'Openstaande facturen',
    
    // Clients
    totalClientsCount: 'totaal klanten',
    addClient: 'Klant toevoegen',
    editClient: 'Klant bewerken',
    addNewClient: 'Nieuwe klant toevoegen',
    clientName: 'Naam',
    phone: 'Telefoon',
    address: 'Adres',
    notes: 'Notities',
    hourlyRate: 'Uurtarief',
    searchClients: 'Klanten zoeken...',
    noClientsYet: 'Nog geen klanten. Voeg uw eerste klant toe!',
    noClientsFound: 'Geen klanten gevonden die overeenkomen met uw zoekopdracht',
    clientDeleted: 'Klant verwijderd',
    clientAdded: 'Klant toegevoegd',
    clientUpdated: 'Klant bijgewerkt',
    deleteConfirm: 'Verwijderen',
    cancel: 'Annuleren',
    edit: 'Bewerken',
    delete: 'Verwijderen',
    viewProjects: 'Bekijk projecten',
    clientProjects: 'Klantprojecten',
    noProjectsForClient: 'Nog geen projecten voor deze klant',
    totalHours: 'Totale uren',
    totalBillable: 'Totaal factureerbaar',
    logHours: 'Uren loggen',
    addExpense: 'Kosten toevoegen',
    
    // Projects
    totalProjectsCount: 'totaal projecten',
    newProject: 'Nieuw project',
    editProject: 'Project bewerken',
    createNewProject: 'Nieuw project aanmaken',
    projectName: 'Projectnaam',
    client: 'Klant',
    selectClient: 'Selecteer een klant',
    description: 'Beschrijving',
    startDate: 'Startdatum',
    endDate: 'Einddatum',
    status: 'Status',
    active: 'Actief',
    inProgress: 'In uitvoering',
    onHold: 'In de wacht',
    completed: 'Voltooid',
    searchProjects: 'Projecten zoeken...',
    noProjectsYet: 'Nog geen projecten. Maak uw eerste project aan!',
    noProjectsFound: 'Geen projecten gevonden die overeenkomen met uw zoekopdracht',
    projectDeleted: 'Project verwijderd',
    projectCreated: 'Project aangemaakt',
    projectUpdated: 'Project bijgewerkt',
    all: 'Alles',
    unknownClient: 'Onbekende klant',
    selectProject: 'Selecteer een project',
    project: 'Project',
    
    // Intakes
    totalIntakesCount: 'totaal intakes',
    newIntake: 'Nieuwe intake',
    editIntake: 'Intake bewerken',
    serviceType: 'Type dienst',
    stage: 'Fase',
    lead: 'Lead',
    consultation: 'Consultatie',
    offerSent: 'Offerte verzonden',
    offerAccepted: 'Offerte geaccepteerd',
    noIntakes: 'Geen intakes',
    intakeDeleted: 'Intake verwijderd',
    intakeCreated: 'Intake aangemaakt',
    intakeUpdated: 'Intake bijgewerkt',
    moveTo: 'Verplaatsen naar',
    leads: 'Leads',
    
    // Calendar
    eventsScheduled: 'evenementen gepland',
    addEvent: 'Evenement toevoegen',
    editEvent: 'Evenement bewerken',
    newEvent: 'Nieuw evenement',
    eventTitle: 'Titel evenement',
    start: 'Start',
    end: 'Einde',
    linkToClient: 'Koppelen aan klant (optioneel)',
    color: 'Kleur',
    sage: 'Salie',
    clay: 'Klei',
    stone: 'Steen',
    blue: 'Blauw',
    none: 'Geen',
    today: 'Vandaag',
    eventDeleted: 'Evenement verwijderd',
    eventCreated: 'Evenement aangemaakt',
    eventUpdated: 'Evenement bijgewerkt',
    more: 'meer',
    
    // Finances
    trackIncomeExpenses: 'Volg uw inkomsten en uitgaven',
    addTransaction: 'Transactie toevoegen',
    newTransaction: 'Nieuwe transactie',
    type: 'Type',
    income: 'Inkomsten',
    expense: 'Uitgave',
    amount: 'Bedrag',
    date: 'Datum',
    category: 'Categorie',
    selectCategory: 'Selecteer categorie',
    optionalNotes: 'Optionele notities',
    saveTransaction: 'Transactie opslaan',
    transactionDeleted: 'Transactie verwijderd',
    incomeRecorded: 'Inkomsten geregistreerd',
    expenseRecorded: 'Uitgave geregistreerd',
    noTransactionsYet: 'Nog geen transacties',
    transactions: 'Transacties',
    expenses: 'Uitgaven',
    monthlyOverview: 'Maandelijks overzicht',
    
    // Time & Expenses
    timeAndExpenses: 'Tijd & Kosten',
    trackTimeAndExpenses: 'Registreer tijd en kosten voor uw projecten',
    timeEntries: 'Tijdregistraties',
    expenseEntries: 'Kostenregistraties',
    addTimeEntry: 'Tijd toevoegen',
    addExpenseEntry: 'Kosten toevoegen',
    hoursWorked: 'Gewerkte uren',
    hours: 'uren',
    expenseType: 'Type kosten',
    cost: 'Kosten',
    products: 'Producten',
    travel: 'Reizen',
    parking: 'Parkeren',
    materials: 'Materialen',
    noTimeEntries: 'Nog geen tijdregistraties',
    noExpenseEntries: 'Nog geen kostenregistraties',
    timeEntryAdded: 'Tijdregistratie toegevoegd',
    timeEntryDeleted: 'Tijdregistratie verwijderd',
    expenseEntryAdded: 'Kostenregistratie toegevoegd',
    expenseEntryDeleted: 'Kostenregistratie verwijderd',
    
    // Invoicing
    invoices: 'Facturen',
    manageInvoices: 'Beheer en genereer facturen',
    createInvoice: 'Factuur aanmaken',
    newInvoice: 'Nieuwe factuur',
    invoiceNumber: 'Factuurnummer',
    dueDate: 'Vervaldatum',
    draft: 'Concept',
    sent: 'Verzonden',
    paid: 'Betaald',
    hoursAmount: 'Urenbedrag',
    totalAmount: 'Totaalbedrag',
    generateInvoice: 'Factuur genereren',
    exportPdf: 'Exporteren naar PDF',
    markAsSent: 'Markeren als verzonden',
    markAsPaid: 'Markeren als betaald',
    invoiceCreated: 'Factuur aangemaakt',
    invoiceUpdated: 'Factuur bijgewerkt',
    invoiceDeleted: 'Factuur verwijderd',
    noInvoicesYet: 'Nog geen facturen',
    viewDetails: 'Details bekijken',
    invoiceDetails: 'Factuurdetails',
    lineItems: 'Regelitems',
    subtotal: 'Subtotaal',
    
    // Categories
    serviceFee: 'Servicekosten',
    productSale: 'Productverkoop',
    referralBonus: 'Verwijzingsbonus',
    other: 'Overig',
    supplies: 'Benodigdheden',
    marketing: 'Marketing',
    software: 'Software',
    insurance: 'Verzekering',
    office: 'Kantoor',
    
    // Common
    required: 'verplicht',
    save: 'Opslaan',
    update: 'Bijwerken',
    create: 'Aanmaken',
    add: 'Toevoegen',
    loading: 'Laden...',
    close: 'Sluiten',
    perHour: 'per uur',
    total: 'Totaal',
    from: 'Van',
    to: 'Tot',
    
    // Settings
    settings: 'Instellingen',
    profile: 'Profiel',
    accountSettings: 'Accountinstellingen',
    accountInfo: 'Accountinformatie',
    memberSince: 'Lid sinds',
    updateProfile: 'Profiel bijwerken',
    changePassword: 'Wachtwoord wijzigen',
    currentPassword: 'Huidig wachtwoord',
    newPassword: 'Nieuw wachtwoord',
    confirmPassword: 'Bevestig wachtwoord',
    passwordsNoMatch: 'Wachtwoorden komen niet overeen',
    passwordUpdated: 'Wachtwoord succesvol bijgewerkt',
    profileUpdated: 'Profiel succesvol bijgewerkt',
    deleteAccount: 'Account verwijderen',
    deleteAccountWarning: 'Deze actie kan niet ongedaan worden gemaakt. Al uw gegevens worden permanent verwijderd.',
    confirmDelete: 'Typ DELETE om te bevestigen',
    accountDeleted: 'Account verwijderd',
    dangerZone: 'Gevarenzone',
    // Tasks
    tasks: 'Taken',
    newTask: 'Nieuwe Taak',
    editTask: 'Taak bewerken',
    taskTitle: 'Taaknaam',
    taskCreated: 'Taak aangemaakt',
    taskUpdated: 'Taak bijgewerkt',
    taskDeleted: 'Taak verwijderd',
    noTasks: 'Nog geen taken',
    totalTasksCount: 'totaal taken',
    selectProject: 'Selecteer project',
    taskOverview: 'Takenoverzicht',
    reportAnIssue: 'Een probleem melden',
    signInWithGoogle: 'Inloggen met Google',
    signUpWithGoogle: 'Aanmelden met Google',
    orContinueWith: 'of doorgaan met e-mail',
    googleAccountNote: 'Ingelogd met Google',
    passwordNotAvailable: 'Wachtwoord wijzigen is niet beschikbaar voor Google accounts',
    intakeDate: 'Intake datum',
    followUpDate: 'Opvolgdatum',
    followUp: 'Opvolging',
    timeline: 'Tijdlijn',
    projectTimeline: 'Project Tijdlijn',
    noProjectDates: 'Nog geen projecten met datums',
    addDatesToProjects: 'Voeg start-/einddatums toe om de tijdlijn te zien',
    beforeAfter: 'Voor & Na',
    followUpReminders: 'Opvolg Herinneringen',
    noFollowUps: 'Geen komende opvolgingen',
    dueToday: 'Vandaag',
    today: 'Vandaag',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
